import { useMemo, useState } from 'react';
import { PrinterIcon } from '@heroicons/react/24/outline';
import type { CalendarAppointment } from '@odo/shared-ui';
import Spinner from '../Spinner';
import { useToast } from '../../providers/ToastProvider';
import { captureAppointmentsPdfError } from '../../observability/sentry';
import { downloadBlob } from '../../utils/downloadBlob';
import { selectUpcoming } from './selectUpcoming';

interface OpenAppointmentsPdfButtonProps {
    /** Already canceled-filtered; this button applies the upcoming + search filters itself. */
    appointments: CalendarAppointment[];
    search: string;
    todayIso: string;
}

const PDF_TITLE = 'Upcoming appointments';
const ENABLED_LABEL = 'Print upcoming appointments';
const EMPTY_LABEL = 'No upcoming appointments to print';
const BUSY_LABEL = 'Preparing the PDF';
const POPUP_BLOCKED_MESSAGE = 'Your browser blocked the new tab, so the PDF was downloaded instead';
const FAILURE_MESSAGE = 'Could not generate the appointments PDF. Please try again.';
// Revoking as soon as the tab is pointed at the blob cancels its load; an idle minute is plenty.
const BLOB_URL_TTL_MS = 60_000;

function OpenAppointmentsPdfButton({
    appointments,
    search,
    todayIso
}: OpenAppointmentsPdfButtonProps) {
    const { showToast } = useToast();
    const [isGenerating, setIsGenerating] = useState(false);

    const upcoming = useMemo(
        () => selectUpcoming(appointments, search, todayIso),
        [appointments, search, todayIso]
    );
    const hasNothingToOpen = upcoming.length === 0;

    const handleOpen = async () => {
        // Opened before the await: rendering is async and popups need the click's gesture.
        const tab = window.open('', '_blank');
        setIsGenerating(true);
        try {
            // Deep import: keeps @react-pdf/renderer out of the main bundle until asked for.
            const { buildAppointmentsPdf } = await import('@odo/shared-ui/appointments/pdf');
            const blob = await buildAppointmentsPdf({
                title: PDF_TITLE,
                appointments: upcoming,
                todayIso
            });
            // `closed` too: rendering takes seconds, and a user left staring at the blank
            // tab often shuts it — assigning `location` on it would then silently do nothing.
            if (!tab || tab.closed) {
                downloadBlob(blob, `Upcoming-Appointments-${todayIso}.pdf`);
                showToast(POPUP_BLOCKED_MESSAGE, 'success');
                return;
            }
            const url = URL.createObjectURL(blob);
            tab.location.href = url;
            window.setTimeout(() => URL.revokeObjectURL(url), BLOB_URL_TTL_MS);
        } catch (error) {
            tab?.close();
            captureAppointmentsPdfError(error);
            showToast(FAILURE_MESSAGE, 'error');
        } finally {
            setIsGenerating(false);
        }
    };

    const label = isGenerating ? BUSY_LABEL : hasNothingToOpen ? EMPTY_LABEL : ENABLED_LABEL;

    return (
        <button
            type="button"
            onClick={() => void handleOpen()}
            disabled={hasNothingToOpen || isGenerating}
            aria-label={label}
            aria-busy={isGenerating}
            title={label}
            className="h-9 w-9 grid place-items-center rounded-lg text-gray-400 hover:bg-gray-50 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-400"
        >
            {isGenerating ? (
                <Spinner className="h-[17px] w-[17px]" />
            ) : (
                <PrinterIcon className="h-[17px] w-[17px]" />
            )}
        </button>
    );
}

export default OpenAppointmentsPdfButton;
